import { GetCopropertyByContractResponse } from "api/coproperty"
import { useAppSelector } from "app/hooks"
import { GenericLoading } from "components/ui/Loader"
import { NsKey } from "enums/NsKey"
import { Sizes } from "enums/sizes"
import { GenericSurveyPreview } from "features/subscription-process/profiling-process/components/Survey/GenericSurveyPreview"
import { ContextCode, SurveyName, useGenericSurvey } from "features/subscription-process/profiling-process/hooks/useGenericSurvey"
import { selectAnswers } from "features/subscription-process/profiling-process/slices/genericSelectors"
import { ProfilingMode } from "features/subscription-process/profiling-process/slices/types/enums"
import { useDialog } from "hooks/useDialog"
import { useDynamicTranslation } from "hooks/useDynamicTranslation"
import { ClipboardList } from "lucide-react"
import { FC, useCallback, useEffect, useState } from "react"
import { copropertiesApi } from "services/swagger-api"
import { useContractInfosState } from "../ContractInfosContext"
import { CopropertyOverviewBody } from "./CopropertyOverviewBody"

/**
 * Onglet « Copropriété » du détail d'un contrat MRI : bandeau d'identité, chiffres clés,
 * puis la description de la copropriété (terrain, natures, bâtiments) et les déclarations
 * (questionnaire d'appréciation du risque en dialog, contrat en cours, sinistralité) — le
 * pendant de l'onglet « Informations du bien » des contrats CBNO/GLI.
 */
export const ContractCopropertyOverview: FC = () => {
  const { contractId } = useContractInfosState()
  const { t } = useDynamicTranslation([NsKey.Common])
  const [response, setResponse] = useState<GetCopropertyByContractResponse>()
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const { initSurvey, mapToSurveyFormValue } = useGenericSurvey()
  const answers = useAppSelector(selectAnswers)
  const { openBlankDialog } = useDialog()

  // Le questionnaire copro (mode Group, comme dans le parcours) doit être chargé pour que
  // les réponses brutes soient traduites en libellés de questions et de réponses.
  useEffect(() => {
    initSurvey(ContextCode.Coproperty, SurveyName.Coproperty, ProfilingMode.Group)
  }, [initSurvey])

  useEffect(() => {
    const getCoproperty = async () => {
      try {
        setIsLoading(true)
        const result = await copropertiesApi.getCopropertyByContractId({ contractId })
        setResponse(result)
        if (result.surveyAnswers?.length)
          mapToSurveyFormValue(result.surveyAnswers)
      } catch (error) {
        console.error("Erreur lors de la récupération de la copropriété du contrat :", error)
      } finally {
        setIsLoading(false)
      }
    }
    getCoproperty()
  }, [contractId, mapToSurveyFormValue])

  const openSurvey = useCallback(() => {
    openBlankDialog(
      { size: Sizes["3xl"] },
      <div className="flex flex-col gap-4 p-6">
        <h3 className="text-lg font-semibold text-primary">{t("COMMON.LABELS.SURVEY_RESPONSES")}</h3>
        <GenericSurveyPreview surveyForm={answers as any} />
      </div>
    )
  }, [answers, openBlankDialog, t])

  if (isLoading) {
    return <div className="relative flex justify-center items-center p-8">
      <GenericLoading isParentSized />
    </div>
  }

  const coproperty = response?.coproperty
  if (!coproperty) {
    return <div className="relative flex justify-center items-center p-8 text-sm text-gray-500">
      Aucune copropriété associée à ce contrat.
    </div>
  }

  const hasSurvey = Boolean(response?.surveyAnswers?.length)

  return (
    <CopropertyOverviewBody
      coproperty={coproperty}
      questionnaireAction={
        hasSurvey && (
          <button
            type="button"
            onClick={openSurvey}
            className="flex items-center gap-1.5 text-[13px] font-medium text-primary hover:underline"
          >
            <ClipboardList className="h-4 w-4" />
            Afficher le questionnaire
          </button>
        )
      }
    />
  )
}
